import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Label,
} from "reactstrap";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import BreadCrumb from "Components/Common/BreadCrumb";
import Loader from "Components/Common/Loader";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import type { RootState } from "store";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import ModuleContainer from "./moduleContainer";
import ModuleForm from "./moduleform";
import { handleValidDate, handleValidTime } from "helpers/common_helper";

import NewDeleteModal from "Components/Common/NewDeleteModal";
import { toast } from "react-toastify";

import {
  fetchModulesThunk,
  addModuleThunk,
  deleteModuleThunk,
  fetchApplicationsThunk,
} from "slices/RBAC/thunk";

import { Link, Navigate, useNavigate } from "react-router-dom";

const selectRBACState = (state: RootState) => (state as any).rbac;

const rbacSelector = createSelector(selectRBACState, (rbac) => ({
  modules: rbac.modules,
  applications: rbac.applications,
  loading: rbac.loading,
}));

const ModuleMasterPage: React.FC = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { modules, applications, loading } = useSelector(rbacSelector);

  const moduleList = modules || [];
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);

  const loadModuleList = useCallback(() => {
    const applicationId = selectedApplicationId || 0;
    return dispatch(fetchModulesThunk(applicationId));
  }, [dispatch, selectedApplicationId]);

  useEffect(() => {
    dispatch(fetchApplicationsThunk());
  }, [dispatch]);

  useEffect(() => {
    const applicationId = selectedApplicationId || 0;
    dispatch(fetchModulesThunk(applicationId));
  }, [dispatch, selectedApplicationId]);

  // Filter to show only active applications
  const activeApplications = React.useMemo(() => {
    if (!Array.isArray(applications)) return [];
    return applications.filter((app: any) => 
      app.status?.toLowerCase() === "active"
    );
  }, [applications]);

  const applicationOptions = activeApplications.map((app) => ({
    label: app.applicationname,
    value: app.applicationid,
  }));

  const handleApplicationChange = (selectedOption: { value: number; label: string } | null) => {
    if (selectedOption) {
      setSelectedApplicationId(selectedOption.value);
    } else {
      setSelectedApplicationId(null);
    }
  };

  const navigate = useNavigate();

  const handleDeleteClick = (id: number) => {
    setSelectedModuleId(id);
    setDeleteModalOpen(true);
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        moduleid: Yup.number()
          .required("Module ID is required")
          .max(9999, "Maximum 4 digits allowed")
          .test(
            "max-digits",
            "Maximum 4 digits allowed",
            function (value) {
              if (!value) return true;
              return value.toString().length <= 4;
            }
          ),
        applicationid: Yup.number().required("Application is required"),
        modulename: Yup.string()
          .required("Module name is required")
          .matches(/^[a-zA-Z ]*$/, "Only alphabets and spaces are allowed")
          .max(70, "Maximum 70 characters allowed"),
        description: Yup.string()
          .required("Description is required")
          .matches(/^[a-zA-Z ]*$/, "Only alphabets and spaces are allowed")
          .max(100, "Maximum 100 characters allowed"),
      }),
    []
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      moduleid: "",
      applicationid: selectedApplicationId || "",
      modulename: "",
      description: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      // Close modal immediately
      setIsModalOpen(false);
      resetForm();

      try {
        const res = await dispatch(
          addModuleThunk({
            moduleid: Number(values.moduleid) || 0,
            applicationid: Number(values.applicationid) || 1,
            modulename: values.modulename,
            description: values.description,
          })
        );
        if (!res?.success) {
          toast.error(res?.message || "Failed to add module");
          return;
        }
        toast.success("Module added successfully!");
        const applicationId = Number(values.applicationid) || selectedApplicationId || 0;
        if (selectedApplicationId) {
          await dispatch(fetchModulesThunk(selectedApplicationId));
        } else {
          await dispatch(fetchModulesThunk(applicationId));
        }
      } catch (err) {
        console.error("Error saving module:", err);
        toast.error("An error occurred. Please try again.");
      }
    },
  });

  const toggleModal = () => {
    if (!isModalOpen) {
      validation.resetForm({
        values: {
          moduleid: "",
          applicationid: selectedApplicationId || "",
          modulename: "",
          description: "",
        },
      });
      if (selectedApplicationId) {
        validation.setFieldValue("applicationid", selectedApplicationId);
      }
    }
    setIsModalOpen(!isModalOpen);
  };

  const handleRowClick = useCallback((row: any) => {}, []);

  const filteredModules = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return moduleList;
    return moduleList.filter((t: any) =>
      t.modulename?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      t.applicationname?.toLowerCase().includes(q)
    );
  }, [moduleList, searchQuery]);

  const columns = useMemo(
    () => [
      {
        header: "Module ID",
        accessorKey: "moduleid",
        cell: ({ row }: any) => {
          const id = row.original.moduleid;
          return `#ID${String(id).padStart(3, "0")}`;
        },
      },
      { header: "Application Name", accessorKey: "applicationname" },
      { header: "Module Name", accessorKey: "modulename" },
      { header: "Description", accessorKey: "description" },
      {
        header: "Created On",
        accessorKey: "createdon",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const value = cell.getValue();
          return value ? (
            <>
              {handleValidDate(value)},{" "}
              <small className="text-muted">{handleValidTime(value)}</small>
            </>
          ) : (
            <>N/A</>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: (cell: any) => {
          const value = cell.getValue();
          const lowerVal = value?.toString().toLowerCase();
          const statusColor =
            lowerVal === "active" ? "#159E3A" : "#A65F00";

          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: "500",
                color: statusColor,
                border: `1px solid ${statusColor}`,
                textTransform: "capitalize",
              }}
            >
              {value}
            </span>
          );
        },
      },
      { header: "Created By", accessorKey: "createdby" },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (cellProps: any) => {
          const row = cellProps.row.original;
  
          return (
            <div className="d-flex justify-content-center align-items-center">
              <Button
                color=""
                className="btn btn-icon btn-soft-danger btn-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(row.moduleid);  
                }}
                title="Delete"
              >
                <i className="ri-delete-bin-line"></i>
              </Button>
            </div>
          );
        },
      },
    ],
    [handleDeleteClick]
  );

  return (
    <div className="page-content">
      <Container fluid className="border pt-4 rounded-4 bg-light mb-3 ms-3 pe-4">
        <BreadCrumb title="Module Master" pageTitle={["RBAC", "Module Master"]} />

        <Row className="align-items-center g-3 d-flex justify-content-between pb-4 pe-3">
          <Col md={6}>
            <div style={{ width: "100%" }}>
              <Select
                options={applicationOptions}
                placeholder="Select Application"
                value={applicationOptions.find((opt) => opt.value === selectedApplicationId) || null}
                onChange={handleApplicationChange}
                classNamePrefix="react-select"
                isClearable
              />
            </div>
          </Col>
          <Col md={6} className="text-end d-flex align-items-center justify-content-end">
            <Button color="primary" onClick={toggleModal}>
              + Add New
            </Button>  
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <Card className="border-0 shadow-none">
              <CardBody>
                {!loading ? (
                  filteredModules.length > 0 ? (
                    <ModuleContainer
                      columns={columns}
                      data={filteredModules}
                      customPageSize={10000}
                      limit={10000}
                      setPage={() => {}}
                      onRowClick={handleRowClick}
                      tableClass="align-middle border-bottom rounded-pill"
                      theadClass="bg-primary text-white"
                      divClass="table-responsive table-card"
                    />
                  ) : (
                    <div className="text-center p-4">
                      No records found.
                    </div>
                  )
                ) : (
                  <Loader />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          Add New Module
        </ModalHeader>
        <form onSubmit={validation.handleSubmit}>
          <ModalBody>
            <ModuleForm 
              validation={validation}
              applications={applications}
              selectedApplicationId={selectedApplicationId}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              type="submit"
              color="primary"
            >
              Add
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <NewDeleteModal
        show={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Module"
        message="Are you sure you want to delete this Module?"
        onConfirm={async () => {
          if (selectedModuleId) {
            setDeleteModalOpen(false);
            const moduleid = selectedModuleId;
            setSelectedModuleId(null);
            try {
              const res = await dispatch(
                deleteModuleThunk(moduleid)
              );
              if (!res?.success) {
                toast.error(res?.message || "Failed to delete module");
                return;
              }
              toast.success("Module deleted successfully!");
              const applicationId = selectedApplicationId || 0;
              await dispatch(fetchModulesThunk(applicationId));
            } catch (err) {
              console.error("Error deleting module:", err);
              toast.error("An error occurred. Please try again.");
            }
          }
        }}
      />
    </div>
  );
};

export default ModuleMasterPage;

